import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  type SelectProps,
  type TableProps,
} from '@cloudscape-design/components';
import { fetchTenants, type Tenant } from '../api/tenants';
import {
  fetchVersions,
  deployToTenant,
  checkDeployStatus,
  triggerPostDeploy,
  type StackVersion,
  type DeployResult,
  type CheckStatusResult,
  type PostDeployResult,
  type StackEvent,
} from '../api/stack-versions';
import {
  fetchDeployments,
  provisionSaasTenant,
  type RoleDeployment,
} from '../api/deployments';

export function ProductDeployments() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedTenantId = searchParams.get('tenant') || '';

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<SelectProps.Option | null>(null);
  const [deployments, setDeployments] = useState<RoleDeployment[]>([]);
  const [loading, setLoading] = useState(true);

  // Deploy modal
  const [showDeploy, setShowDeploy] = useState(false);
  const [deployProduct, setDeployProduct] = useState<RoleDeployment | null>(null);
  const [versions, setVersions] = useState<StackVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<SelectProps.Option | null>(null);
  const [deployAccountId, setDeployAccountId] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState<DeployResult | null>(null);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);
  const [statusResult, setStatusResult] = useState<CheckStatusResult | null>(null);
  const [postDeployTriggered, setPostDeployTriggered] = useState(false);
  const [postDeployResult, setPostDeployResult] = useState<PostDeployResult | null>(null);
  const [hostingMode, setHostingMode] = useState<'self-deployed' | 'saas-hosted'>('self-deployed');

  // Shared hosting stack info
  const SHARED_HOSTING = {
    tenantId: 'ac78c8d7-373f-448d-8266-3bbce6e5e9df', // saas-prod in 730335467631
    accountId: '730335467631',
    apiUrl: 'https://k1xpluixw5.execute-api.us-east-1.amazonaws.com/prod',
    frontendUrl: 'https://app.aeospc3.com',
    userPoolId: 'us-east-1_psmXSVzZg',
    userPoolClientId: '1hnd1uugbqn335dpj1s4iiuddo',
  };

  useEffect(() => {
    fetchTenants().then((res) => {
      setTenants(res.tenants);
      if (preselectedTenantId) {
        const t = res.tenants.find((t) => t.tenantId === preselectedTenantId);
        if (t) setSelectedTenant({ label: `${t.name} — ${t.companyName}`, value: t.tenantId });
      }
    }).finally(() => setLoading(false));
  }, [preselectedTenantId]);

  useEffect(() => {
    if (!selectedTenant?.value) { setDeployments([]); return; }
    setLoading(true);
    fetchDeployments(selectedTenant.value)
      .then((res) => setDeployments(res.deployments))
      .finally(() => setLoading(false));
  }, [selectedTenant]);

  const tenantOptions: SelectProps.Options = tenants.map((t) => ({
    label: `${t.name} — ${t.companyName}`,
    value: t.tenantId,
    description: t.awsAccountId ? `AWS: ${t.awsAccountId}` : 'No AWS account',
  }));

  // Only show deployments that have roles validated (integrated/deployed)
  const readyDeployments = deployments.filter(
    (d) => d.status === 'integrated' || d.status === 'deployed' || d.status === 'parameters_set'
  );

  async function openDeploy(dep: RoleDeployment) {
    setDeployProduct(dep);
    setDeployResult(null);
    setDeployError(null);
    setSelectedVersion(null);
    setStatusResult(null);
    setPostDeployTriggered(false);
    setPostDeployResult(null);
    setHostingMode('self-deployed');
    const tenant = tenants.find((t) => t.tenantId === selectedTenant?.value);
    setDeployAccountId(dep.stackOutputs?.customerAccountId || tenant?.awsAccountId || '');
    setShowDeploy(true);
    try {
      const res = await fetchVersions(dep.productId);
      setVersions(res.versions);
      const current = res.versions.find((v) => v.status === 'current');
      if (current) setSelectedVersion({ label: `v${current.version} (current)`, value: current.version });
      else if (res.versions.length > 0) {
        const first = res.versions[0];
        setSelectedVersion({ label: `v${first.version} (${first.status})`, value: first.version });
      }
    } catch { setVersions([]); }
  }

  /**
   * Provision a SaaS-hosted PC3 tenant WITHOUT requiring a role deployment.
   * Opens the deploy modal pre-set to saas-hosted with a synthetic aeos-pc3
   * product, so a tenant can be stood up on the shared stack immediately.
   * Role deployment stays available as a later enhance motion.
   */
  function openSaasProvision() {
    const existing = deployments.find((d) => d.productId === 'aeos-pc3');
    const synthetic: RoleDeployment = existing ?? {
      tenantId: selectedTenant?.value ?? '',
      productId: 'aeos-pc3',
      productName: 'AEOS PC3',
      vendor: 'gdna',
      status: 'pending',
      parameters: {},
      stackOutputs: {},
      validated: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setDeployProduct(synthetic);
    setDeployResult(null);
    setDeployError(null);
    setSelectedVersion(null);
    setStatusResult(null);
    setPostDeployTriggered(false);
    setPostDeployResult(null);
    setHostingMode('saas-hosted');
    const tenant = tenants.find((t) => t.tenantId === selectedTenant?.value);
    setDeployAccountId(tenant?.awsAccountId || SHARED_HOSTING.accountId);
    setShowDeploy(true);
  }

  async function handleDeploy() {
    if (!deployProduct || !selectedTenant?.value) return;

    // SaaS-hosted mode — just provision tenant on shared stack
    if (hostingMode === 'saas-hosted') {
      setDeploying(true);
      setDeployError(null);
      try {
        // Fetch the current stable version from the registry
        const { fetchVersions: fetchVer } = await import('../api/stack-versions');
        let stableVersion = 'latest';
        try {
          const verRes = await fetchVer(deployProduct.productId);
          const stable = verRes.versions.find((v) => v.status === 'current');
          if (stable) stableVersion = stable.version;
        } catch { /* fallback to 'latest' */ }

        // Create admin user on shared pool (admin@pc3.gdna.io — gdna access)
        const apiBase = import.meta.env.VITE_OPS_API_URL || 'https://k6wq9xbvc8.execute-api.us-east-1.amazonaws.com/dev';
        const tenant = tenants.find((t) => t.tenantId === selectedTenant.value);
        const response = await fetch(`${apiBase}/ops/post-deploy-steps`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId: selectedTenant.value,
            customerAccountId: SHARED_HOSTING.accountId,
            version: 'shared',
            stackName: 'gdna-aeos-pc3-ce9a2746',
            contactEmail: tenants.find((t) => t.tenantId === selectedTenant.value)?.contactEmail || '',
            step: 'create_admin',
          }),
        });
        const result = await response.json();

        // Also create the customer's designated user
        let customerUserMsg = '';
        if (tenant?.contactEmail && tenant.contactEmail !== 'admin@pc3.gdna.io') {
          try {
            const custResp = await fetch(`${apiBase}/ops/tenants/${selectedTenant.value}/products`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                action: 'create_saas_user',
                email: tenant.contactEmail,
                name: tenant.name || tenant.companyName,
                userPoolId: SHARED_HOSTING.userPoolId,
                customerAccountId: SHARED_HOSTING.accountId,
              }),
            });
            customerUserMsg = ` + ${tenant.contactEmail}`;
          } catch { /* customer user creation is best-effort */ }
        }

        setDeployResult({
          status: 'completed',
          action: 'create',
          stackName: 'saas-hosted',
          version: stableVersion,
          customerAccountId: tenant?.awsAccountId || SHARED_HOSTING.accountId,
          message: `Tenant provisioned on shared hosting (v${stableVersion}). Admin: ${result.results?.create_admin || 'admin@pc3.gdna.io'}${customerUserMsg}. Site: ${SHARED_HOSTING.frontendUrl}`,
        });
        // Update deployment record with hosting mode (upsert — no role required)
        await provisionSaasTenant(selectedTenant.value, deployProduct.productId, {
          hostingMode: 'saas-hosted',
          sharedStackId: SHARED_HOSTING.tenantId,
          FrontendUrl: SHARED_HOSTING.frontendUrl,
          ApiUrl: SHARED_HOSTING.apiUrl,
          UserPoolId: SHARED_HOSTING.userPoolId,
          UserPoolClientId: SHARED_HOSTING.userPoolClientId,
          customerAccountId: tenant?.awsAccountId || SHARED_HOSTING.accountId,
          hostingAccountId: SHARED_HOSTING.accountId,
          version: stableVersion,
        });
        // Auto-setup custom domain via wildcard cert
        try {
          const cdResp = await fetch(`${import.meta.env.VITE_OPS_API_URL || 'https://k6wq9xbvc8.execute-api.us-east-1.amazonaws.com/dev'}/ops/custom-domains/saas-setup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tenantId: selectedTenant.value }),
          });
          const cdResult = await cdResp.json();
          if (cdResult.customUrl) {
            setDeployResult((prev) => prev ? { ...prev, message: `${prev.message}\nCustom domain: ${cdResult.customUrl}` } : prev);
          }
        } catch { /* custom domain is best-effort during provisioning */ }
      } catch (e: unknown) {
        setDeployError(e instanceof Error ? e.message : 'Provisioning failed');
      } finally { setDeploying(false); }
      return;
    }

    // Self-deployed mode — existing CFN deploy flow
    if (!selectedVersion?.value || !deployAccountId) return;
    setDeploying(true);
    setDeployError(null);
    try {
      const result = await deployToTenant(deployProduct.productId, selectedVersion.value, {
        tenantId: selectedTenant.value,
        customerAccountId: deployAccountId,
      });
      setDeployResult(result);
      setStatusResult(null); // Clear any stale status
      // Start auto-polling status every 5 seconds
      if (result.status === 'deploying') {
        const pollInterval = setInterval(async () => {
          try {
            const status = await checkDeployStatus(deployProduct.productId, {
              tenantId: selectedTenant.value!,
              customerAccountId: deployAccountId,
              stackName: result.stackName,
            });
            // Skip stale ROLLBACK/DELETE statuses from previous failed stacks
            if (status.stackStatus.includes('ROLLBACK') || status.stackStatus.includes('DELETE')) {
              // Don't show — the deploy function is cleaning up the old stack
              return;
            }
            setStatusResult(status);
            if (status.stackStatus.includes('COMPLETE') || status.stackStatus.includes('FAILED')) {
              clearInterval(pollInterval);
              // Auto-trigger post-deploy on success using the step runner
              if ((status.stackStatus === 'CREATE_COMPLETE' || status.stackStatus === 'UPDATE_COMPLETE') && !postDeployTriggered) {
                setPostDeployTriggered(true);
                const tenant = tenants.find((t) => t.tenantId === selectedTenant.value);
                fetch(`${import.meta.env.VITE_OPS_API_URL || 'https://k6wq9xbvc8.execute-api.us-east-1.amazonaws.com/dev'}/ops/post-deploy-steps`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    tenantId: selectedTenant.value!,
                    customerAccountId: deployAccountId,
                    version: selectedVersion.value!,
                    stackName: result.stackName,
                    contactEmail: tenant?.contactEmail || '',
                    contactName: tenant?.name,
                  }),
                }).then((r) => r.json()).then((pdResult) => {
                  setPostDeployResult({
                    status: pdResult.status,
                    adminEmail: pdResult.results?.create_admin,
                    pcLinkStatus: pdResult.results?.pc_probe,
                  });
                }).catch(() => {});
              }
              // Reload deployments
              if (selectedTenant?.value) {
                const res = await fetchDeployments(selectedTenant.value);
                setDeployments(res.deployments);
              }
            }
          } catch { /* keep polling */ }
        }, 5000);
        // Stop polling after 10 minutes max
        setTimeout(() => clearInterval(pollInterval), 600000);
      }
    } catch (e: unknown) {
      setDeployError(e instanceof Error ? e.message : 'Deploy failed');
    } finally { setDeploying(false); }
  }

  async function handleCheckStatus(dep: RoleDeployment) {
    const acct = dep.stackOutputs?.customerAccountId || tenants.find((t) => t.tenantId === selectedTenant?.value)?.awsAccountId || '';
    if (!acct || !selectedTenant?.value) return;
    setCheckingStatus(dep.productId);
    setStatusResult(null);
    try {
      const result = await checkDeployStatus(dep.productId, {
        tenantId: selectedTenant.value,
        customerAccountId: acct,
        stackName: dep.stackOutputs?.stackName,
      });
      setStatusResult(result);
      // Reload deployments to pick up updated record
      if (selectedTenant?.value) {
        const res = await fetchDeployments(selectedTenant.value);
        setDeployments(res.deployments);
      }
    } catch { /* ignore */ }
    finally { setCheckingStatus(null); }
  }

  const columns: TableProps.ColumnDefinition<RoleDeployment>[] = [
    { id: 'product', header: 'Product', cell: (item) => item.productName, width: 140 },
    { id: 'vendor', header: 'Vendor', cell: (item) => item.vendor === 'gdna' ? 'GDNA' : '3rd Party', width: 80 },
    {
      id: 'roleStatus', header: 'Role Status', width: 130,
      cell: (item) => {
        const map: Record<string, 'success' | 'in-progress' | 'pending' | 'error'> = {
          integrated: 'success', deployed: 'success', parameters_set: 'in-progress',
          cfn_launched: 'in-progress', pending: 'pending', validation_failed: 'error', failed: 'error',
        };
        return <StatusIndicator type={map[item.status] || 'pending'}>{item.status.replace('_', ' ')}</StatusIndicator>;
      },
    },
    {
      id: 'productStatus', header: 'Product Status', width: 150,
      cell: (item) => {
        const isModelB = item.productId !== 'aeos-pc3' && item.productId !== 'acp';
        if (isModelB) {
          return item.status === 'integrated'
            ? <StatusIndicator type="success">SaaS Active</StatusIndicator>
            : <StatusIndicator type="pending">Pending Setup</StatusIndicator>;
        }
        const stackName = item.stackOutputs?.stackName;
        const deployedAt = item.stackOutputs?.deployedAt;
        const checkedAt = item.stackOutputs?.checkedAt;
        if (!stackName && !deployedAt) {
          return <StatusIndicator type="stopped">Not deployed</StatusIndicator>;
        }
        if (checkedAt) {
          return <StatusIndicator type="success">Live</StatusIndicator>;
        }
        if (deployedAt) {
          return <StatusIndicator type="in-progress">Deployed — unverified</StatusIndicator>;
        }
        return <StatusIndicator type="pending">Unknown</StatusIndicator>;
      },
    },
    {
      id: 'version', header: 'Version', width: 90,
      cell: (item) => {
        const ver = item.stackOutputs?.deployedVersion || item.stackOutputs?.version;
        return ver ? <code style={{ fontSize: 12 }}>v{ver}</code> : '—';
      },
    },
    {
      id: 'account', header: 'AWS Account', width: 130,
      cell: (item) => item.stackOutputs?.customerAccountId
        ? <code style={{ fontSize: 12 }}>{item.stackOutputs.customerAccountId}</code>
        : '—',
    },
    {
      id: 'actions', header: 'Actions', width: 240,
      cell: (item) => {
        // Model B products (no versioned templates — vendor-hosted SaaS like One-CT, Archera)
        const isModelB = item.productId !== 'aeos-pc3' && item.productId !== 'acp';
        if (isModelB) {
          return (
            <SpaceBetween direction="horizontal" size="xs">
              <StatusIndicator type={item.status === 'integrated' ? 'success' : 'pending'}>
                {item.status === 'integrated' ? 'SaaS Provisioned' : 'Pending Setup'}
              </StatusIndicator>
            </SpaceBetween>
          );
        }
        if (item.status === 'integrated' || item.status === 'deployed') {
          return (
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="primary" onClick={() => openDeploy(item)}>Deploy Product</Button>
              <Button loading={checkingStatus === item.productId} onClick={() => handleCheckStatus(item)}>Check Status</Button>
            </SpaceBetween>
          );
        }
        if (item.status === 'parameters_set' || item.status === 'cfn_launched') {
          return (
            <SpaceBetween direction="horizontal" size="xs">
              <StatusIndicator type="warning">Role not validated</StatusIndicator>
              <Button onClick={() => navigate(`/ops/deployments/${selectedTenant?.value}`)}>Fix Roles</Button>
            </SpaceBetween>
          );
        }
        return <Button onClick={() => navigate(`/ops/deployments/${selectedTenant?.value}`)}>Setup Role</Button>;
      },
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Deploy a SaaS tenant instantly (no role required), or deploy the full stack into a customer account. Role deployment is optional for SaaS and can be added later."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => { if (selectedTenant?.value) { setLoading(true); fetchDeployments(selectedTenant.value).then((r) => setDeployments(r.deployments)).finally(() => setLoading(false)); } }}>Refresh</Button>
            <Button variant="primary" disabled={!selectedTenant?.value} onClick={openSaasProvision}>Provision SaaS Tenant</Button>
            <Button onClick={() => navigate(`/ops/customer-deployments${selectedTenant?.value ? `?tenant=${selectedTenant.value}` : ''}`)}>Customer Deployments →</Button>
          </SpaceBetween>
        }
      >Product Deployments</Header>

      <Container>
        <FormField label="Tenant">
          <Select selectedOption={selectedTenant}
            onChange={({ detail }) => setSelectedTenant(detail.selectedOption)}
            options={tenantOptions} placeholder="Select a tenant..." filteringType="auto" />
        </FormField>
      </Container>

      {!selectedTenant?.value ? (
        <Box textAlign="center" padding="xxl" color="text-status-inactive">Select a tenant to view product deployments.</Box>
      ) : (
        <>
          {statusResult && (
            <Alert type={statusResult.isComplete ? 'success' : statusResult.stackStatus.includes('FAILED') ? 'error' : 'info'}
              dismissible onDismiss={() => setStatusResult(null)}>
              <SpaceBetween size="s">
                <StatusIndicator type={statusResult.isComplete ? 'success' : statusResult.stackStatus.includes('FAILED') ? 'error' : 'in-progress'}>
                  {statusResult.isComplete ? 'Stack deployed successfully' : statusResult.message}
                </StatusIndicator>
                {statusResult.isComplete && Object.keys(statusResult.outputs).length > 0 && (
                  <>
                    <ColumnLayout columns={2} variant="text-grid">
                      {statusResult.outputs.FrontendUrl && (
                        <SpaceBetween size="xxs">
                          <Box variant="awsui-key-label" fontSize="body-s">Frontend</Box>
                          <a href={statusResult.outputs.FrontendUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>{statusResult.outputs.FrontendUrl}</a>
                        </SpaceBetween>
                      )}
                      {statusResult.outputs.ApiUrl && (
                        <SpaceBetween size="xxs">
                          <Box variant="awsui-key-label" fontSize="body-s">API</Box>
                          <code style={{ fontSize: 12 }}>{statusResult.outputs.ApiUrl}</code>
                        </SpaceBetween>
                      )}
                      {statusResult.outputs.UserPoolId && (
                        <SpaceBetween size="xxs">
                          <Box variant="awsui-key-label" fontSize="body-s">Cognito Pool</Box>
                          <code style={{ fontSize: 12 }}>{statusResult.outputs.UserPoolId}</code>
                        </SpaceBetween>
                      )}
                      {statusResult.outputs.CloudFrontDistributionId && (
                        <SpaceBetween size="xxs">
                          <Box variant="awsui-key-label" fontSize="body-s">CloudFront</Box>
                          <code style={{ fontSize: 12 }}>{statusResult.outputs.CloudFrontDistributionId}</code>
                        </SpaceBetween>
                      )}
                      {statusResult.outputs.MCPEndpoint && (
                        <SpaceBetween size="xxs">
                          <Box variant="awsui-key-label" fontSize="body-s">MCP Endpoint</Box>
                          <code style={{ fontSize: 12 }}>{statusResult.outputs.MCPEndpoint}</code>
                        </SpaceBetween>
                      )}
                      {statusResult.outputs.CoreTableName && (
                        <SpaceBetween size="xxs">
                          <Box variant="awsui-key-label" fontSize="body-s">DynamoDB</Box>
                          <code style={{ fontSize: 12 }}>{statusResult.outputs.CoreTableName}</code>
                        </SpaceBetween>
                      )}
                    </ColumnLayout>
                    {statusResult.pcLinkStatus === 'not_linked' && (
                      <Alert type="warning" statusIconAriaLabel="Warning">
                        Partner Central account not linked. The customer needs to link AWS account {statusResult.outputs.customerAccountId || ''} in the Partner Central console.
                      </Alert>
                    )}
                  </>
                )}
              </SpaceBetween>
            </Alert>
          )}
        <Table
          columnDefinitions={columns}
          items={readyDeployments}
          loading={loading}
          loadingText="Loading deployments..."
          trackBy="productId"
          variant="container"
          stripedRows
          empty={
            <Box textAlign="center" padding="l">
              <Box variant="p">No role deployments yet for this tenant.</Box>
              <Box variant="p" color="text-status-inactive" margin={{ bottom: 's' }}>
                You can provision a SaaS tenant now without a role, or set up role deployments for a full customer-account install.
              </Box>
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" onClick={openSaasProvision}>Provision SaaS Tenant</Button>
                <Button onClick={() => navigate(`/ops/deployments/${selectedTenant.value}`)}>
                  Setup Role Deployments
                </Button>
              </SpaceBetween>
            </Box>
          }
          header={<Header counter={`(${readyDeployments.length})`}>Deployments</Header>}
        />
        </>
      )}

      {/* Deploy modal */}
      <Modal visible={showDeploy} onDismiss={() => setShowDeploy(false)}
        header={`Deploy ${deployProduct?.productName || ''}`}
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowDeploy(false)}>Close</Button>
          {!deployResult && (
            <Button variant="primary" onClick={handleDeploy} loading={deploying}
              disabled={hostingMode === 'self-deployed' ? (!selectedVersion || !deployAccountId || deployAccountId.length !== 12) : false}>
              {hostingMode === 'saas-hosted' ? 'Provision Tenant' : 'Deploy Stack'}
            </Button>
          )}
        </SpaceBetween></Box>}>
        <SpaceBetween size="m">
          {deployError && <Alert type="error">{deployError}</Alert>}
          {deployResult && (
            <Alert type={deployResult.status === 'no_changes' ? 'info' : 'success'}>
              {deployResult.message}
              {deployResult.stackName && (
                <Box variant="p" margin={{ top: 'xs' }}>Stack: <code>{deployResult.stackName}</code> — Action: {deployResult.action}</Box>
              )}
            </Alert>
          )}
          {deployResult && statusResult && (
            <Alert type={
              statusResult.stackStatus === 'DELETE_IN_PROGRESS' ? 'info' :
              statusResult.stackStatus.includes('FAILED') || statusResult.stackStatus.includes('ROLLBACK') ? 'error' :
              statusResult.isComplete ? 'success' : 'info'
            }>
              <StatusIndicator type={
                statusResult.stackStatus === 'DELETE_IN_PROGRESS' ? 'in-progress' :
                statusResult.stackStatus === 'CREATE_IN_PROGRESS' ? 'in-progress' :
                statusResult.isComplete ? 'success' :
                statusResult.stackStatus.includes('FAILED') ? 'error' : 'in-progress'
              }>
                {statusResult.stackStatus === 'DELETE_IN_PROGRESS' ? 'Cleaning up previous failed stack...' :
                 statusResult.stackStatus === 'DELETE_COMPLETE' ? 'Previous stack cleaned up. Creating new stack...' :
                 statusResult.stackStatus}
              </StatusIndicator>
              {statusResult.stackStatus !== 'DELETE_IN_PROGRESS' && statusResult.stackStatus !== 'DELETE_COMPLETE' && (
                <Box variant="p" margin={{ top: 'xs' }}>{statusResult.message}</Box>
              )}
              {statusResult.recentEvents && statusResult.recentEvents.length > 0 && !statusResult.isComplete && (
                <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', fontSize: 12, fontFamily: 'monospace', background: 'rgba(0,0,0,0.15)', borderRadius: 4, padding: 8 }}>
                  {(() => {
                    // Deduplicate: keep highest-priority status per resource
                    // Priority: FAILED > COMPLETE > IN_PROGRESS
                    const priority = (s: string) => s.includes('FAILED') ? 3 : s.includes('COMPLETE') ? 2 : s.includes('IN_PROGRESS') ? 1 : 0;
                    const seen = new Map<string, (typeof statusResult.recentEvents)[number]>();
                    for (const e of (statusResult.recentEvents || []).filter((ev) => !ev.status.includes('ROLLBACK') && !ev.status.includes('DELETE'))) {
                      // Skip the top-level stack itself
                      if (e.resource === statusResult.stackName?.split('/')[0]) continue;
                      if (e.type === 'AWS::CloudFormation::Stack' && e.resource !== 'BaseStack') continue;
                      const existing = seen.get(e.resource);
                      if (!existing || priority(e.status) > priority(existing.status)) {
                        seen.set(e.resource, e);
                      }
                    }
                    return Array.from(seen.values())
                      .sort((a, b) => {
                        const pa = priority(a.status);
                        const pb = priority(b.status);
                        if (pa !== pb) return pb - pa;
                        return a.resource.localeCompare(b.resource);
                      })
                      .map((e, i) => {
                        const isComplete = e.status.includes('COMPLETE');
                        const isFailed = e.status.includes('FAILED');
                        const isProgress = e.status.includes('IN_PROGRESS');
                        const color = isFailed ? '#ef4444' : isComplete ? '#22c55e' : isProgress ? '#3b82f6' : '#9ca3af';
                        const icon = isFailed ? '✗' : isComplete ? '✓' : isProgress ? '⟳' : '·';
                        return (
                          <div key={i} style={{ padding: '1px 0', color, display: 'flex', gap: 6, lineHeight: '18px' }}>
                            <span style={{ width: 14, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{e.resource}</span>
                            <span style={{ flexShrink: 0, opacity: 0.7 }}>{e.status.replace(/^(CREATE|UPDATE|DELETE)_/, '')}</span>
                          </div>
                        );
                      });
                  })()}
                </div>
              )}
            </Alert>
          )}
          {postDeployResult && (
            <Alert type={postDeployResult.status === 'completed' ? 'success' : postDeployResult.status === 'failed' ? 'error' : 'info'}>
              <StatusIndicator type={postDeployResult.status === 'completed' ? 'success' : postDeployResult.status === 'failed' ? 'error' : 'in-progress'}>
                Post-deploy: {postDeployResult.status}
              </StatusIndicator>
              {postDeployResult.customUrl && <Box variant="p" margin={{ top: 'xs' }}>Custom domain: <a href={postDeployResult.customUrl} target="_blank" rel="noreferrer">{postDeployResult.customUrl}</a></Box>}
              {postDeployResult.adminEmail && <Box variant="p">Admin: {postDeployResult.adminEmail}</Box>}
              {postDeployResult.pcLinkStatus && <Box variant="p">PC Link: {postDeployResult.pcLinkStatus}</Box>}
            </Alert>
          )}
          {!deployResult && (
            <>
              <FormField label="Hosting Mode">
                <Select
                  selectedOption={{ label: hostingMode === 'self-deployed' ? '🏗️ Self-Deployed (customer account)' : '☁️ SaaS Hosted (gdna account)', value: hostingMode }}
                  onChange={({ detail }) => {
                    const mode = detail.selectedOption.value as 'self-deployed' | 'saas-hosted';
                    setHostingMode(mode);
                    if (mode === 'saas-hosted') setDeployAccountId(SHARED_HOSTING.accountId);
                  }}
                  options={[
                    { label: '🏗️ Self-Deployed (customer account)', value: 'self-deployed', description: 'Full stack in customer AWS account. Customer owns data and infrastructure.' },
                    { label: '☁️ SaaS Hosted (gdna account)', value: 'saas-hosted', description: 'Shared hosting. Instant provisioning. Customer gets custom domain + isolated tenant.' },
                  ]}
                />
              </FormField>
              {hostingMode === 'saas-hosted' ? (
                <Alert type="info">
                  <SpaceBetween size="xs">
                    <Box variant="p">Customer will be provisioned on the shared PC3 instance. No CFN deploy needed.</Box>
                    <Box variant="p">Frontend: <code>{SHARED_HOSTING.frontendUrl}</code></Box>
                    <Box variant="p">A custom domain (<code>{'{tenant}'}.aeospc3.com</code>) will be assigned automatically.</Box>
                  </SpaceBetween>
                </Alert>
              ) : (
                <>
                  <FormField label="Version">
                    <Select selectedOption={selectedVersion}
                      onChange={({ detail }) => setSelectedVersion(detail.selectedOption)}
                      options={versions.filter((v) => v.status !== 'deprecated').map((v) => ({ label: `v${v.version} (${v.status})`, value: v.version, description: v.description }))}
                      placeholder="Select version" />
                  </FormField>
                  <FormField label="Customer AWS Account ID" constraintText="12-digit account ID">
                    <Input value={deployAccountId} onChange={({ detail }) => setDeployAccountId(detail.value)}
                      placeholder="123456789012" inputMode="numeric" />
                  </FormField>
                  <Alert type="warning">
                    This assumes the deploy role in the customer account and creates/updates a CloudFormation stack.
                  </Alert>
                </>
              )}
            </>
          )}
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
